import { trackPendoEvent } from 'services/PendoService';

/**
 * The multi-run vote events behind Ryan's 3X readout (EN-10922).
 *
 * Ryan's 24 Sep call — keep 3X, review after a month, pull it if it suppresses
 * 2X in multi-way pots — needs every vote split heads-up vs multi-way and
 * offered vs chosen. Before this the client emitted nothing for the vote at
 * all, so the readout could not be produced from what shipped.
 *
 * PROVISIONAL names and shapes: Cory owns the data definitions (asked on
 * EN-10856 21 Sep and EN-10922 24 Sep) and may rename them; the payloads are
 * deliberately flat so a rename is a mapping, not a migration. Production
 * stages only (the Pendo allowlist), no player identity in the payload (Pendo
 * already keys the visitor on playerGUID), and analytics never blocks play.
 *
 *   prompt_shown  a seat was asked: its role and the highest count on offer
 *   cast          it answered: the count chosen (1 = RUN IT 1X, the initiator's
 *                 "once" and a responder's decline) and seconds to decide
 *   timeout       its window ran out on the vote clock with no answer from it
 *   resolved      once per hand, from the initiator's client: what was offered
 *                 (0 = the initiator never chose, 1 = chose 1X) and how many
 *                 boards actually ran
 */
export type MultiRunVoteRole = 'initiator' | 'responder';
export type MultiRunVoteContest = 'heads-up' | 'multi-way';

type HandFacts = {
  gameType: string;
  contest: MultiRunVoteContest;
  /** Seats still playing for the pot when the vote opened. */
  contestingSeats: number;
};

export type MultiRunVoteEvent =
  | ({
      name: 'multi_run_vote_prompt_shown';
      role: MultiRunVoteRole;
      offeredMax: number;
    } & HandFacts)
  | ({
      name: 'multi_run_vote_cast';
      role: MultiRunVoteRole;
      offeredMax: number;
      chosen: number;
      secondsToDecide: number;
    } & HandFacts)
  | ({ name: 'multi_run_vote_timeout'; role: MultiRunVoteRole; offeredMax: number } & HandFacts)
  | ({ name: 'multi_run_vote_resolved'; offered: number; resolvedRuns: number } & HandFacts);

/**
 * Report `event` for the player at this seat. A table is mounted more than once
 * (carousel panel + grid cell) and each copy watches the same frames, so `key`
 * (game, window, seat, role, step) is the dedupe key PendoService checks; scoped
 * to the player, another account at the same seat is a different event.
 * PendoService holds the event until that player is the identified visitor and
 * drops it on logout.
 */
export const reportMultiRunVoteEventOnce = (
  key: string,
  event: MultiRunVoteEvent,
  playerGUID: string
): void => {
  const { name, ...properties } = event;
  trackPendoEvent({ playerGUID, eventName: name, properties, dedupeKey: `${playerGUID}:${key}` });
};
